import {forwardRef, useState, useCallback, useRef} from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { Box, Stack, SvgIcon, Typography, Drawer, IconButton, Button, BoxProps, Alert } from '@mui/material';
import {  Close as CloseIcon, LightbulbOutlined as LightBulbIcon, ChevronRight as ChevronRightIcon} from '@mui/icons-material';
import {
  ChatConversation,
  ChatComposer,
  ChatComposerTextArea,
  ChatMessage,
  ChatMessageAvatar,
  ChatMessageContent,
  ChatMessageGroup,
  ChatMessageList,
} from '@mui/x-chat';
import { type ChatUser, ChatProvider, useMessageIds, useMessage, useChatComposer, useChat, type ChatTextMessagePart } from '@mui/x-chat/headless';
import { createAdapter, createInitialConversation, initialMessages} from './adapter';
import { type AuthUser } from '../../context/UserContext'
import { useHype } from '../../context/HypeContext'
import HypeAvatar from '../../assets/sparkles.svg?react';
import hypeAvatarURL from '../../assets/sparkles.svg?url';
import { grey } from '@mui/material/colors';

const SendIcon = () => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ width: '1em', height: '1em' }}
    >
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}


const ChoiceBubble = forwardRef<HTMLDivElement, BoxProps>(function ChoiceBubble(props, ref) {
  const {children, ...other} = props
  return (
    <Box
      ref={ref} 
      {...other} 
      sx={{
        marginLeft: '34px',
        gap: 4,
      }}
    >
      <Button
        fullWidth
        variant="outlined"
        disableElevation
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          px: 1.5,
          py: 1,
          bgcolor: 'background.paper',
          borderColor: 'grey.200',
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: 'grey.50',
            borderColor: 'grey.300',
            '& .lightbulb-icon': { color: 'primary.main' },
            '& .chevron-icon': { color: 'text.primary' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, width: '100%' }}>
          <LightBulbIcon
            className="lightbulb-icon"
            sx={{
              fontSize: '0.875rem', // 14px equivalent
              color: 'text.secondary',
              transition: 'color 0.2s',
              flexShrink: 0,
            }}
          />
          
          <Typography
            sx={{
              flex: 1,
              textAlign: 'left',
              color: 'text.secondary',
              fontSize: '0.8125rem', // 13px
            }}
          >
            {children}
          </Typography>

          <ChevronRightIcon
            className="chevron-icon"
            sx={{
              fontSize: '0.875rem',
              color: 'grey.300',
              transition: 'color 0.2s',
            }}
          />
        </Box>
      </Button> 
    </Box>
  );
});


const CustomMessage = (props: {messageId: string, onMessageClick: (messageText: string) => void}) => {
  const {messageId, onMessageClick } = props

  const message = useMessage(messageId);

  const handleMessageClick = () => {
    if(message){
      const textPart = message.parts.find((part) => part.type === "text") as ChatTextMessagePart
      onMessageClick(textPart?.text ?? "")
    }
  }

  const isChoice = messageId.includes('choice')
  const isReasoning = messageId.includes('reasoning')

  const style = message?.role !== 'user' && !isChoice && isReasoning && {
     '.MuiChatMessage-content': {
            '.MuiChatMessage-bubble': {
                bgcolor: 'background.paper',
              },
              textTransform: 'none',
              px: 1.5,
              py: 1,
              bgcolor: 'background.paper',
              borderColor: 'grey.200',
              borderRadius: 1,
          },
  }

  return (
      <ChatMessage messageId={messageId}
        sx={{
          '&.MuiChatMessage-root': {
              paddingInlineEnd: '20px',
          },
          '.MuiChatMessage-avatar':{
            borderRadius: message?.role !== 'user' ? "10px" : "none",
            background: message?.role !== 'user' ? "#5b5fd9" : "none",
            width: '30px',
            height: '30px',
            marginRight: '5px',
            img: message?.role !== 'user' ? {
              color: '#fff',
              width: '50%',
              height: '50%',
            } : {},
          },
          ...style,
        }}
      >
        {!isChoice && <ChatMessageAvatar />}
        {isChoice && <ChatMessageContent
           slots={{
            bubble: ChoiceBubble,
          }}
          onClick={()=>handleMessageClick()}
        >
        </ChatMessageContent>}

        {isReasoning && <ChatMessageContent /> }

        {!isChoice && !isReasoning && <ChatMessageContent />}
      </ChatMessage>
    )
}

const TYPING_INDICATOR_ID = 'hype-typing-indicator';

const TypingIndicator = () => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        py: 1,
        paddingInline: '16px',
        animation: 'hype-typing-in 0.25s ease-out',
        '@keyframes hype-typing-in': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          mr: '5px',
          flexShrink: 0,
          borderRadius: '10px',
          background: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HypeAvatar style={{ color: '#fff', width: '50%', height: '50%' }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 1.25,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.primary.main, 0.7),
              animation: 'hype-typing 1.4s infinite ease-in-out both',
              animationDelay: `${i * 0.18}s`,
              '@keyframes hype-typing': {
                '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Stack>
  );
}

const CustomChat = (props: {userRole: ChatUser}) => {
  const messageIds = useMessageIds();
  const composer = useChatComposer();
  const {sendMessage, error, isStreaming} = useChat()

  const sendText = (text: string, attachments: typeof composer.attachments = []) => {
    sendMessage({
      author: props.userRole,
      parts: [{
        type: 'text',
        text,
      }],
      attachments,
    });
  };

  const handleCustomSend = () => {
    if (!composer.value && composer.attachments.length === 0) return;
    sendText(composer.value, composer.attachments);
    // Clear the composer after submitting
    composer.clear();
  };

  // Clicking a suggested message sends it immediately.
  const handleMessageclick = (message: string) => {
    if(message){
      sendText(message)
    }
  }

  const renderItem = useCallback(
    (params: { id: string }) => {
      // Render the live typing indicator as the last item in the message
      // list so it flows with the conversation, right after the last message.
      if (params.id === TYPING_INDICATOR_ID) {
        return <TypingIndicator key={params.id} />;
      }
      return (
        <ChatMessageGroup key={params.id} messageId={params.id}>
          <CustomMessage messageId={params.id} onMessageClick={handleMessageclick}
          />
        </ChatMessageGroup>
      );
    },
    [handleMessageclick],
  );

  const placeHolder = "Ask me anything…"

  const items = isStreaming ? [...messageIds, TYPING_INDICATOR_ID] : messageIds;

  return (
      <ChatConversation sx={{overflow: 'hidden'}}>
        {/* Huge buffer => always treated as "at bottom" => always auto-scrolls on reply. */}
        <ChatMessageList autoScroll={{ buffer: Number.MAX_SAFE_INTEGER }} renderItem={renderItem} items={items} />
        <Box sx={{paddingTop: '20px'}}>
          <ChatComposer sx={{height: '50px', width: '75%'}}>
            <ChatComposerTextArea placeholder={placeHolder} sx={{scrollbarWidth: 'none'}}/>
              <IconButton
                onClick={handleCustomSend}
                sx={{
                  right: '-50px', 
                  top: '5px',
                  position: 'absolute', 
                  backgroundColor: '#5B5FD9', 
                  color: '#fff',
                  '&:hover': { 
                    backgroundColor: '#383a85',
                  }}} 
                disabled={!composer.value}>
                <SendIcon/>
              </IconButton>
          </ChatComposer>
          {error && <Alert variant='outlined' severity="error">{error?.message}</Alert>}
        </Box>
      </ChatConversation>
  );
}


interface ChatProps {
  user: AuthUser
}

export const HypeChatBox = (props: ChatProps):JSX.Element => {
  const {chat} = useHype()
  const {user} = props
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const adapterContextRef = useRef({ prevResponseId: chat?.prevResponseId ?? null });
  adapterContextRef.current = { prevResponseId: chat?.prevResponseId ?? null };
  const [chatAdapter] = useState(() => createAdapter(adapterContextRef));
  const { email, name, avatarUrl: userAvatar } = user
  const displayName = name || email || '—';
  const theme = useTheme();
  const drawerWidth = sidebarOpen ? 350 : 0;
  const userRole: ChatUser = {
      id: email ?? displayName,
      displayName,
      avatarUrl: userAvatar,
      role: 'user',
    }
  const chatMembers: ChatUser[] = [
    userRole,
    {
      id: 'assistant-1',
      displayName: 'Hype AI',
      avatarUrl: hypeAvatarURL,
      role: 'assistant',
    },
  ]
  const initialConversation = createInitialConversation(chatMembers)

  return (
    <Box sx={{backgroundColor: "#fff"}}>
      <Drawer
          open={sidebarOpen}
          variant="permanent"
          anchor={"right"}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: 'width 200ms ease',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              transition: 'width 200ms ease',
              overflowX: 'hidden',
              backgroundColor: "#fff",
              
            },
          }}
        >
          <Box sx={{padding: 2, display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={2} justifyContent={'flex-start'}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '10px',
                    background: '#5b5fd9',
                  }}
                >
                  <HypeAvatar style={{ color: '#fff', width: '50%', height: '50%'}} 
                  />
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ color: theme.palette.operations.contrastText, display: 'block' }}>
                    Hype AI
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{color: grey[500]}}>
                    Intelligent Assistant
                  </Typography>
                </Box>
                  <div style={{
                      marginLeft: 'auto',
                    }}>
                    <IconButton onClick={() => setSidebarOpen(false)} >
                    <CloseIcon sx={{fontSize: '18px'}} />
                  </IconButton>                  
                </div>
              </Stack>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {sidebarOpen &&
                <ChatProvider
                  adapter={chatAdapter}
                  initialActiveConversationId={initialConversation.id}
                  initialConversations={[initialConversation]}
                  initialMessages={initialMessages}
                  members={chatMembers}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      '*, *::before, *::after': { boxSizing: 'inherit' },
                    }}
                  >
                    <CustomChat userRole={userRole}/>
                </Box>
              </ChatProvider>
             }
            </Box>
          </Box>
        </Drawer>
       {!sidebarOpen && 
       <IconButton
        id="find_me"
        onClick={() => setSidebarOpen(true)}
        sx={{
          width: '40px',
          height: '40px',
          borderRadius: '15px',
          background: '#5b5fd9',
          position: 'fixed',
          right: 25,
          zIndex: 1,
          bottom: 25,
        }}
        >
          <SvgIcon component={HypeAvatar} fontSize={'medium'} inheritViewBox sx={{color: '#5b5fd9', width: "20px",
            height: "20px"}} />
        </IconButton>}
    </Box>
  );
}